import React, { useState } from 'react';
import styled from 'styled-components';
import { useProjectElementListStore } from '../../shared/store/projectElementStore';
import { useEditMode } from '../../shared/hooks/useEditMode';
import { useValidation } from '../../shared/hooks/useValidation';
import { useStandardAlertStore } from '../../shared/store/portal/alertStore';
import { getAlertMessage } from '../../shared/constants/alertMessages';
import { openNewTab } from '../../shared/util/openNewTab';
import { AlertPosition, AlertType, DisplayAlignment, DisplaySize, ImageStyle } from '../../shared/enum/EnumRepository';
import { DocumentData } from '../../shared/dto/EntityRepository';
import { StyledImgProps } from '../../shared/dto/StyleCompRepository';
import { convertS3UrlToCloudFrontUrl } from '../../shared/aws/awsHelper';
import OptimizedMoleculeImg from '../../shared/component/molecule/OptimizedMoleculeImg';
import { parseSupportedVideoUrl } from '../../shared/util/videoUrl';
import { ImgInPeWrapper, WorkImageForLandscape, WorkImageForPortrait } from './WorkImgInPe';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import HintWrapper from '../../shared/component/headless/button/HintWrapper';
import MoleculeInputDiv from '../../shared/component/molecule/MoleculeInputDiv';
import { WorkViewerInfo } from '../../shared/component/headless/input/InputBody';
import { getHintMessage } from '../../shared/constants/hintMessages';
import playIcon from '../../asset/icon/play.png';
import imageIcon from '../../asset/icon/image.png';

export interface DocumentVideoInPeProps {
  peId: string;
  alignment: DisplayAlignment;
  displaySize: DisplaySize;
  data: DocumentData;
  priority: boolean;
  imageStyle: ImageStyle;
  onChangeType?: () => void;
  onVideoUrlChange?: (videoUrl: string) => void;
}

const DocumentVideoInPe: React.FC<DocumentVideoInPeProps> = ({
  peId,
  alignment,
  displaySize,
  data,
  priority,
  imageStyle,
  onChangeType,
  onVideoUrlChange,
}) => {
  const handleImageChange = useProjectElementListStore((s) => s.updateImage);
  const { isEditMode } = useEditMode();
  const { isValidVideoUrl } = useValidation();
  const { enqueueAlert } = useStandardAlertStore();
  const [isHovered, setIsHovered] = useState(false);

  const convertedCloudFrontUrl = convertS3UrlToCloudFrontUrl(data.uploadFile.originUrl);
  const imageRatio = data.uploadFile?.width && data.uploadFile?.height
    ? data.uploadFile.width / data.uploadFile.height
    : undefined;

  const moveToVideoUrl = () => {
    if (!data.videoUrl) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('VIDEO_URL_NULL'),
      });
      return;
    }
    if (!isValidVideoUrl(data.videoUrl)) return;
    const videoUrl = parseSupportedVideoUrl(data.videoUrl);
    if (!videoUrl) return;
    enqueueAlert({
      type: AlertType.CONFIRM,
      position: AlertPosition.TOP,
      content: getAlertMessage('CONFIRM_MOVE_TO_VIDEO_URL_PLATFORM', { platform: videoUrl.platform }),
      callBack: () => openNewTab(videoUrl.url.href),
    });
  };

  const getStyledImage = () => {
    if (imageStyle === ImageStyle.LANDSCAPE) {
      return WorkImageForLandscape as React.ComponentType<StyledImgProps>;
    } else {
      return WorkImageForPortrait as React.ComponentType<StyledImgProps>;
    }
  };

  return (
    <ImgInPeWrapper
      $alignment={alignment || DisplayAlignment.CENTER}
      $displaySize={displaySize || DisplaySize.BIG}
      $imageStyle={imageStyle || ImageStyle.LANDSCAPE}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <ImageWrap>
        <OptimizedMoleculeImg
          srcUrl={convertedCloudFrontUrl}
          alt={data.description || 'Visual Aid'}
          displaySize={displaySize}
          displayAlignment={alignment || DisplayAlignment.CENTER}
          handleChange={(originUrl: string, width: number, height: number) =>
            handleImageChange(peId, originUrl, width, height)
          }
          StyledImg={getStyledImage()}
          priority={priority}
          imageRatio={imageRatio}
        />
        {!isEditMode && (
          <PlayOverlay $isHovered={isHovered}>
            <UniversalIconBtn
              size="standard"
              border={false}
              backgroundImg={playIcon}
              onClick={moveToVideoUrl}
            />
          </PlayOverlay>
        )}
        {isEditMode && onChangeType && (
          <EditTransparentLayer>
            <EditBtnContainer>
              <HintWrapper
                content={getHintMessage('CHANGE_DETAIL_TYPE', { detailType: 'Image' })}
                toLeft={true}
              >
                <UniversalIconBtn
                  size="fit"
                  border={true}
                  backgroundImg={imageIcon}
                  onClick={onChangeType}
                />
              </HintWrapper>
            </EditBtnContainer>
          </EditTransparentLayer>
        )}
      </ImageWrap>
      {isEditMode && onVideoUrlChange && (
        <VideoInfoRow>
          <VideoInfoLabel>Video URL</VideoInfoLabel>
          <MoleculeInputDiv
            value={data.videoUrl ?? ''}
            defaultValue=""
            DefaultDiv={VideoInfoDefaultDiv}
            placeholder="YouTube or Vimeo URL only"
            handleChange={(e) => onVideoUrlChange(e.target.value)}
            inputStyle={WorkViewerInfo}
            StyledDiv={VideoInfoValue}
          />
        </VideoInfoRow>
      )}
    </ImgInPeWrapper>
  );
};

const ImageWrap = styled.div`
  position: relative;
  width: fit-content;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const PlayOverlay = styled.div<{ $isHovered: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  opacity: ${({ $isHovered }) => ($isHovered ? 1 : 0)};
  backdrop-filter: blur(${({ $isHovered }) => ($isHovered ? 1.5 : 0)}px);
  transition: all 0.4s ease-in-out;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const EditTransparentLayer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0.5;
  background: linear-gradient(to right, transparent 50%, ${({ theme }) => theme.colors.color_Gray_06} 150%);
  backdrop-filter: blur(0px);
  &:hover {
    opacity: 1;
    backdrop-filter: blur(2px);
  }
  transition: all 0.6s ease-in-out;
  cursor: default;
`;

const EditBtnContainer = styled.div`
  position: absolute;
  right: 0.5vh;
  top: 0.5vh;
  width: fit-content;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.5vw;
`;

const VideoInfoRow = styled.div`
  width: 0;
  min-width: 100%;
  height: 18px;
  min-height: 18px;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
  ${({ theme }) => theme.typography.Body_04};
`;

const VideoInfoLabel = styled.span`
  min-width: 70px;
  padding-top: 2px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  font-weight: 500;
`;

const VideoInfoDefaultDiv = styled.div`
  width: fit-content;
  color: ${({ theme }) => theme.colors.color_Gray_05} !important;
  ${({ theme }) => theme.typography.Body_04};
  background-color: ${({ theme }) => theme.colors.color_Gray_06};
`;

const VideoInfoValue = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  text-align: left;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
  line-height: 1.2;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_04};
`;

export default DocumentVideoInPe;
